import React, { useEffect, useMemo, useState } from "react";

import {
  createBeefFootprintCalculationSummary,
  createDairyFootprintCalculationSummary,
  createFootprintCalculationSummary,
  createPigletFootprintCalculationSummary,
  createPorkFootprintCalculationSummary,
  getFarmFootprintingCapabilities,
  getRegionalComparisonValue,
  getYearArray,
  isPercentageComplete,
  queryIsLoading,
  sumCropYieldTonnesAndHectares,
  sumFootprintCalculationSummaries,
  useGetFarmDetails,
  useGetFarms,
  useGetFootprintingBeefFootprints,
  useGetFootprintingCalculatedBeefFootprints,
  useGetFootprintingCalculatedCropFootprints,
  useGetFootprintingCalculatedDairyFootprints,
  useGetFootprintingCalculatedFarmFootprints,
  useGetFootprintingCalculatedPigletFootprints,
  useGetFootprintingCalculatedPorkFootprints,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprints,
  useGetFootprintingPigletFootprints,
  useGetFootprintingPorkFootprints,
  useGetPracticeCrops,
  useLocation,
  useTranslation,
} from "utils";

import { ENUM_RESULTS_PAGE_TYPE, KG_PER_TONNE, RESULTS_PAGE_TYPES } from "consts";

import {
  BeefFootprintCalculationSummary,
  CropFootprintCalculationSummary,
  DairyFootprintCalculationSummary,
  PigletFootprintCalculationSummary,
  PorkFootprintCalculationSummary,
} from "models";

import { BarChart, CheckboxSegmented, EmptyStateCard, Flex, Icon, LineChart, MainPage, OutlinedCard, PageSpin, SelectSegmented, Typography } from "components";

import { FootprintingBeefResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingBeefResultTable";
import { FootprintingCropResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingCropResultTable";
import { FootprintingDairyResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingDairyResultTable";
import { FootprintingPieChart } from "pages/app/footprinting/components/resultsComponents/FootprintingPieChart";
import { FootprintingPigletResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingPigletResultTable";
import { FootprintingPorkResultTable } from "pages/app/footprinting/components/resultsComponents/FootprintingPorkResultTable";
import { SummaryTiles } from "pages/app/footprinting/components/resultsComponents/SummaryTiles";

const FootprintingResultsPage: React.FC = () => {
  const { t } = useTranslation([]);
  const location = useLocation();
  const { footprintId: stateFootprintId, year: stateYear } = location.state || {};

  const { currentFarm, isLoading: isGetFarmsLoading } = useGetFarms();
  const farmDetailsQuery = useGetFarmDetails({ farmId: currentFarm?.id });
  const cropDetailsQuery = useGetPracticeCrops({ enabled: true });

  const currentYear = new Date().getFullYear();
  const availableYears = getYearArray(currentFarm?.carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear, currentYear);

  // TODO: Make this always primary data id?
  /** Crop ID in the case of crop, dairy footprint primary data ID in the case of dairy */
  const [selectedId, setSelectedId] = useState<string | null>(stateFootprintId || null);
  const [selectedYear, setSelectedYear] = useState(
    stateYear && availableYears.includes(parseInt(stateYear))
      ? parseInt(stateYear)
      : currentFarm?.carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear
  );

  const carbonProgramHasResultsEnabled =
    currentFarm?.carbonProgram?.platformFeatures.footprinting?.enabled && (currentFarm?.carbonProgram?.platformFeatures.footprinting.results ?? true);

  const { dairyEnabled, beefEnabled, porkEnabled } = getFarmFootprintingCapabilities(currentFarm?.carbonProgram ?? undefined);

  const carbonProgramHasBeefEnabled = carbonProgramHasResultsEnabled && (beefEnabled ?? false);
  const carbonProgramHasDairyEnabled = carbonProgramHasResultsEnabled && (dairyEnabled ?? false);
  const carbonProgramHasPorkEnabled = carbonProgramHasResultsEnabled && (porkEnabled ?? false);
  const carbonProgramHasPigletEnabled = carbonProgramHasResultsEnabled && (porkEnabled ?? false);

  // Get our farm footprint(s) for the current year
  const farmProgressQueries = useGetFootprintingFarmFootprints(currentFarm?.id, availableYears, { enabled: carbonProgramHasResultsEnabled });
  const farmCalculationQueries = useGetFootprintingCalculatedFarmFootprints(currentFarm?.id, availableYears, { enabled: carbonProgramHasResultsEnabled });

  // Get our beef footprint(s) for the current year
  const beefProgressQueries = useGetFootprintingBeefFootprints(currentFarm?.id, availableYears, {
    enabled: carbonProgramHasResultsEnabled && carbonProgramHasBeefEnabled,
  });

  const completedBeefProgressQueries = beefProgressQueries.filter((bpq) => isPercentageComplete(bpq.data?.completionPct ?? 0));
  const beefCalculationQueries = useGetFootprintingCalculatedBeefFootprints(
    currentFarm?.id,
    completedBeefProgressQueries.map((bpq) => bpq.data?.year ?? 0),
    {
      enabled: carbonProgramHasResultsEnabled && carbonProgramHasBeefEnabled,
    }
  );

  // Get our dairy footprint(s) for the current year
  const dairyProgressQueries = useGetFootprintingDairyFootprints(currentFarm?.id, availableYears, {
    enabled: carbonProgramHasResultsEnabled && carbonProgramHasDairyEnabled,
  });

  const completedDairyProgressQueries = dairyProgressQueries.filter((dpq) => isPercentageComplete(dpq.data?.completionPct ?? 0));
  const dairyCalculationQueries = useGetFootprintingCalculatedDairyFootprints(
    currentFarm?.id,
    completedDairyProgressQueries.map((dpq) => dpq.data?.year ?? 0),
    {
      enabled: carbonProgramHasResultsEnabled && carbonProgramHasDairyEnabled,
    }
  );

  // Get our pork footprint(s) for the current year
  const porkProgressQueries = useGetFootprintingPorkFootprints(currentFarm?.id, availableYears, {
    enabled: carbonProgramHasResultsEnabled && carbonProgramHasPorkEnabled,
  });

  const completedPorkProgressQueries = porkProgressQueries.filter((ppq) => isPercentageComplete(ppq.data?.completionPct ?? 0));
  const porkCalculationQueries = useGetFootprintingCalculatedPorkFootprints(
    currentFarm?.id,
    completedPorkProgressQueries.map((ppq) => ppq.data?.year ?? 0),
    {
      enabled: carbonProgramHasResultsEnabled && carbonProgramHasPorkEnabled,
    }
  );

  // Get our piglet footprint(s) for the current year
  const pigletProgressQueries = useGetFootprintingPigletFootprints(currentFarm?.id, availableYears, {
    enabled: carbonProgramHasResultsEnabled && carbonProgramHasPigletEnabled,
  });

  const completedPigletProgressQueries = pigletProgressQueries.filter((ppq) => isPercentageComplete(ppq.data?.completionPct ?? 0));
  const pigletCalculationQueries = useGetFootprintingCalculatedPigletFootprints(
    currentFarm?.id,
    completedPigletProgressQueries.map((ppq) => ppq.data?.year ?? 0),
    {
      enabled: carbonProgramHasResultsEnabled && carbonProgramHasPigletEnabled,
    }
  );

  // Get our crop footprint(s) for the current year
  const cropProgressQueries = useGetFootprintingCropFootprints(
    currentFarm?.id,
    availableYears,
    currentFarm?.carbonProgram?.farmLevelFootprinting?.programCrops,
    {
      enabled: carbonProgramHasResultsEnabled,
    }
  );

  const completedCropProgressQueries = cropProgressQueries.filter((cpq) => cpq.data && isPercentageComplete(cpq.data.completionPct));
  const completedCropCalculationQueries = useGetFootprintingCalculatedCropFootprints(
    currentFarm?.id,
    availableYears,
    completedCropProgressQueries.map((cpq) => cpq.data?.cropId).filter((id) => id !== undefined),
    { enabled: carbonProgramHasResultsEnabled }
  );

  const isLoading =
    !currentFarm ||
    !currentFarm.id ||
    isGetFarmsLoading ||
    [
      cropDetailsQuery,
      ...completedCropProgressQueries,
      ...completedCropCalculationQueries.filter((ccq) => ccq.data?.year === selectedYear),
      ...farmCalculationQueries.filter((fcq) => fcq.data?.year === selectedYear),
    ].some(queryIsLoading);

  const footprintType: ENUM_RESULTS_PAGE_TYPE | null =
    selectedId === null
      ? RESULTS_PAGE_TYPES.allCrops
      : completedBeefProgressQueries.find((bpq) => bpq.data?.footprint?.id === selectedId)
      ? RESULTS_PAGE_TYPES.beef
      : completedDairyProgressQueries.find((dpq) => dpq.data?.footprint?.id === selectedId)
      ? RESULTS_PAGE_TYPES.dairy
      : completedPorkProgressQueries.find((ppq) => ppq.data?.footprint?.id === selectedId)
      ? RESULTS_PAGE_TYPES.pork
      : completedPigletProgressQueries.find((ppq) => ppq.data?.footprint?.id === selectedId)
      ? RESULTS_PAGE_TYPES.piglet
      : // TODO: Convert crops to also use primary data IDs?
      completedCropProgressQueries.find((cpq) => cpq.data?.cropId === selectedId && cpq.data.year === selectedYear)
      ? RESULTS_PAGE_TYPES.crop
      : null;

  useEffect(() => {
    const selectableQueryIds = [
      ...completedCropProgressQueries.map((cpq) => cpq.data?.cropId),
      ...completedBeefProgressQueries.map((bpq) => bpq.data?.footprint?.id),
      ...completedDairyProgressQueries.map((cdq) => cdq.data?.footprint?.id),
      ...completedPorkProgressQueries.map((ppq) => ppq.data?.footprint?.id),
      ...completedPigletProgressQueries.map((ppq) => ppq.data?.footprint?.id),
    ];

    if (selectableQueryIds.length === 1 && selectedId !== selectableQueryIds[0]) {
      setSelectedId(selectableQueryIds[0] ?? null);
    }
  }, [
    completedBeefProgressQueries,
    completedCropProgressQueries,
    completedDairyProgressQueries,
    completedPigletProgressQueries,
    completedPorkProgressQueries,
    selectedId,
  ]);

  const selectedCropCalculationSummariesForAllYears = useMemo(
    () =>
      footprintType !== "crop"
        ? []
        : (completedCropProgressQueries
            .filter((cpq) => cpq.data !== undefined && cpq.data.cropId === selectedId && isPercentageComplete(cpq.data?.completionPct ?? 0))
            .map((cpq) => {
              const ccq = completedCropCalculationQueries.find((ccq) => ccq.data?.cropId === cpq.data?.cropId && cpq.data?.year === ccq.data?.year);
              const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === cpq.data?.year);
              if (!farmDetailsQuery.data || !cpq.data || !ccq || !fcq) {
                return { year: cpq.data!.year, footprint: null };
              }

              return {
                year: cpq.data.year,
                cropId: cropDetailsQuery.data?.find((cd) => cd.id === cpq.data.cropId)?.id,
                cropName: cropDetailsQuery.data?.find((cd) => cd.id === cpq.data.cropId)?.name,
                footprint: createFootprintCalculationSummary(farmDetailsQuery.data, fcq.data, ccq.data, cpq?.data?.footprint),
              };
            })
            .filter((r) => r.footprint !== null)
            .sort((a, b) => a.year - b.year) as Array<{ year: number; cropId: string; cropName: string; footprint: CropFootprintCalculationSummary }>),
    [
      completedCropCalculationQueries,
      completedCropProgressQueries,
      cropDetailsQuery.data,
      farmCalculationQueries,
      farmDetailsQuery.data,
      footprintType,
      selectedId,
    ]
  );

  const selectedBeefCalculationSummariesForAllYears = useMemo(
    () =>
      footprintType !== RESULTS_PAGE_TYPES.beef
        ? []
        : (completedBeefProgressQueries
            .filter((bpq) => bpq.data !== undefined && isPercentageComplete(bpq.data?.completionPct ?? 0))
            .map((bpq) => {
              const bcq = beefCalculationQueries.find((bcq) => bpq.data?.year === bcq.data?.year);
              const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === bpq.data?.year);
              if (!farmDetailsQuery.data || !bpq.data || !bcq || !fcq) {
                return { year: bpq.data!.year, footprint: null };
              }

              return {
                year: bpq.data.year,
                footprint: createBeefFootprintCalculationSummary(farmDetailsQuery.data, fcq.data, bcq.data, bpq.data),
              };
            })
            .filter((r) => r.footprint !== null)
            .sort((a, b) => a.year - b.year) as Array<{ year: number; footprint: BeefFootprintCalculationSummary }>),
    [beefCalculationQueries, completedBeefProgressQueries, farmCalculationQueries, farmDetailsQuery.data, footprintType]
  );

  const selectedDairyCalculationSummariesForAllYears = useMemo(
    () =>
      footprintType !== RESULTS_PAGE_TYPES.dairy
        ? []
        : (completedDairyProgressQueries
            .filter((cpq) => cpq.data !== undefined && isPercentageComplete(cpq.data?.completionPct ?? 0))
            .map((dpq) => {
              const dcq = dairyCalculationQueries.find((cdq) => dpq.data?.year === cdq.data?.year);
              const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === dpq.data?.year);
              if (!farmDetailsQuery.data || !dpq.data || !dcq || !fcq) {
                return { year: dpq.data!.year, footprint: null };
              }

              return {
                year: dpq.data.year,
                footprint: createDairyFootprintCalculationSummary(farmDetailsQuery.data, fcq.data, dcq.data, dpq.data),
              };
            })
            .filter((r) => r.footprint !== null)
            .sort((a, b) => a.year - b.year) as Array<{ year: number; footprint: DairyFootprintCalculationSummary }>),
    [completedDairyProgressQueries, dairyCalculationQueries, farmCalculationQueries, farmDetailsQuery.data, footprintType]
  );

  const selectedPorkCalculationSummariesForAllYears = useMemo(
    () =>
      footprintType !== RESULTS_PAGE_TYPES.pork
        ? []
        : (completedPorkProgressQueries
            .filter((ppq) => ppq.data !== undefined && isPercentageComplete(ppq.data?.completionPct ?? 0))
            .map((ppq) => {
              const pcq = porkCalculationQueries.find((cpq) => ppq.data?.year === cpq.data?.year);
              const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === ppq.data?.year);
              if (!farmDetailsQuery.data || !ppq.data || !pcq || !fcq) {
                return { year: ppq.data!.year, footprint: null };
              }

              return {
                year: ppq.data.year,
                footprint: createPorkFootprintCalculationSummary(farmDetailsQuery.data, fcq.data, pcq.data),
              };
            })
            .filter((r) => r.footprint !== null)
            .sort((a, b) => a.year - b.year) as Array<{ year: number; footprint: PorkFootprintCalculationSummary }>),
    [completedPorkProgressQueries, farmCalculationQueries, farmDetailsQuery.data, footprintType, porkCalculationQueries]
  );

  const selectedPigletCalculationSummariesForAllYears = useMemo(
    () =>
      footprintType !== RESULTS_PAGE_TYPES.piglet
        ? []
        : (completedPigletProgressQueries
            .filter((ppq) => ppq.data !== undefined && isPercentageComplete(ppq.data?.completionPct ?? 0))
            .map((ppq) => {
              const pcq = pigletCalculationQueries.find((cpq) => ppq.data?.year === cpq.data?.year);
              const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === ppq.data?.year);
              if (!farmDetailsQuery.data || !ppq.data || !pcq || !fcq) {
                return { year: ppq.data!.year, footprint: null };
              }

              return {
                year: ppq.data.year,
                footprint: createPigletFootprintCalculationSummary(farmDetailsQuery.data, fcq.data, pcq.data),
              };
            })
            .filter((r) => r.footprint !== null)
            .sort((a, b) => a.year - b.year) as Array<{ year: number; footprint: PigletFootprintCalculationSummary }>),
    [completedPigletProgressQueries, farmCalculationQueries, farmDetailsQuery.data, footprintType, pigletCalculationQueries]
  );

  const footprintCalculationSummary = useMemo(() => {
    if (isLoading || !farmDetailsQuery.data) {
      return null;
    }

    // "All crops"
    if (footprintType === RESULTS_PAGE_TYPES.allCrops) {
      const { totalYieldTonnes, totalFootprintedHectares } = sumCropYieldTonnesAndHectares(
        completedCropProgressQueries.filter((ccq) => ccq.data !== undefined).map((cpq) => cpq.data?.footprint)
      );

      return sumFootprintCalculationSummaries(
        completedCropCalculationQueries
          .filter((ccq) => ccq.data !== undefined)
          .map((ccq) => {
            const progressQuery = completedCropProgressQueries.find((cpq) => cpq.data?.year === selectedYear && cpq.data.cropId === ccq.data.cropId);
            return createFootprintCalculationSummary(
              farmDetailsQuery.data,
              farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data,
              ccq.data,
              progressQuery?.data?.footprint
            );
          })
          .filter((calcSummary) => calcSummary !== null),
        KG_PER_TONNE * totalYieldTonnes,
        totalFootprintedHectares
      );
    }

    // "Selected crop" was already calculated above
    if (footprintType === RESULTS_PAGE_TYPES.crop)
      return selectedCropCalculationSummariesForAllYears.find((sccs) => sccs.year === selectedYear && sccs.cropId === selectedId)?.footprint ?? null;

    // Beef case
    if (footprintType === RESULTS_PAGE_TYPES.beef) {
      return createBeefFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data,
        beefCalculationQueries.find((bcq) => bcq.data?.year === selectedYear)?.data,
        beefProgressQueries.find((bpq) => bpq.data?.footprint?.id === selectedId)?.data
      );
    }

    // Dairy case
    if (footprintType === RESULTS_PAGE_TYPES.dairy) {
      return createDairyFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data,
        dairyCalculationQueries.find((dcq) => dcq.data?.year === selectedYear)?.data,
        dairyProgressQueries.find((dpq) => dpq.data?.footprint?.id === selectedId)?.data
      );
    }

    // Pork case
    if (footprintType === RESULTS_PAGE_TYPES.pork) {
      return createPorkFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data,
        porkCalculationQueries.find((pcq) => pcq.data?.year === selectedYear)?.data
      );
    }

    // Piglet case
    if (footprintType === RESULTS_PAGE_TYPES.piglet) {
      return createPigletFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries.find((fcq) => fcq.data?.year === selectedYear)?.data,
        pigletCalculationQueries.find((pcq) => pcq.data?.year === selectedYear)?.data
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selectedYear, selectedBeefCalculationSummariesForAllYears, selectedCropCalculationSummariesForAllYears]);

  const regionalValue = useMemo(
    () => getRegionalComparisonValue(footprintType, farmDetailsQuery.data?.country, selectedId),
    [farmDetailsQuery.data?.country, footprintType, selectedId]
  );

  const regionalValueComparisonChange =
    regionalValue != null && footprintCalculationSummary?.overall.perKgYieldCO2e != null
      ? ((footprintCalculationSummary.overall.perKgYieldCO2e - regionalValue) / regionalValue) * 100
      : undefined;

  if (isLoading) {
    return (
      <Flex justify="center">
        <PageSpin />
      </Flex>
    );
  }

  if (!currentFarm?.carbonProgram?.farmLevelFootprinting) {
    return (
      <Flex justify="center">
        <Typography variant="h1">{t("footprinting:footprintingNotEnabled")}</Typography>
      </Flex>
    );
  }

  const thisYearCompletedCropProgressQueries = completedCropProgressQueries.filter((cpq) => cpq.data?.year === selectedYear);

  const hasResults =
    completedBeefProgressQueries.length > 0 ||
    completedCropProgressQueries.length > 0 ||
    completedDairyProgressQueries.length > 0 ||
    completedPorkProgressQueries.length > 0 ||
    completedPigletProgressQueries.length > 0;

  const hasResultsForSelectedYear =
    thisYearCompletedCropProgressQueries.length > 0 ||
    beefProgressQueries.some((bpq) => bpq.data?.year === selectedYear && isPercentageComplete(bpq.data.completionPct)) ||
    dairyProgressQueries.some((dpq) => dpq.data?.year === selectedYear && isPercentageComplete(dpq.data.completionPct)) ||
    porkProgressQueries.some((ppq) => ppq.data?.year === selectedYear && isPercentageComplete(ppq.data.completionPct)) ||
    pigletProgressQueries.some((ppq) => ppq.data?.year === selectedYear && isPercentageComplete(ppq.data.completionPct));

  const showEmptyState = footprintCalculationSummary === null || (footprintType === RESULTS_PAGE_TYPES.allCrops && !hasResultsForSelectedYear);

  const navigationCrumbs = {
    crumbs: [
      { name: currentFarm?.name || t("modules:navigation.overview.farm"), replacedByFarmSwitch: true },
      { name: t("footprinting:footprintingLandingPage.breadcrumb"), link: "footprinting" },
      { name: t("footprinting:resultsPage.breadcrumb") },
    ],
  };

  const yoyChartDataset =
    footprintType === RESULTS_PAGE_TYPES.crop
      ? selectedCropCalculationSummariesForAllYears
      : footprintType === RESULTS_PAGE_TYPES.beef
      ? selectedBeefCalculationSummariesForAllYears
      : footprintType === RESULTS_PAGE_TYPES.pork
      ? selectedPorkCalculationSummariesForAllYears
      : footprintType === RESULTS_PAGE_TYPES.piglet
      ? selectedPigletCalculationSummariesForAllYears
      : selectedDairyCalculationSummariesForAllYears;

  /** Pork results are per kg slaughter weight and piglet results per kg live weight, so each needs its own unit labelling */
  const resultsChartCopyKeys =
    footprintType === RESULTS_PAGE_TYPES.pork
      ? {
          barChartTooltip: "footprinting:resultsPage.barChart.titleTooltipPork",
          barChartSubtitle: "footprinting:resultsPage.barChart.subtitlePork",
          yearOnYearSubtitle: "footprinting:resultsPage.yearOnYearComparison.subtitlePork",
        }
      : footprintType === RESULTS_PAGE_TYPES.piglet
      ? {
          barChartTooltip: "footprinting:resultsPage.barChart.titleTooltipPiglet",
          barChartSubtitle: "footprinting:resultsPage.barChart.subtitlePiglet",
          yearOnYearSubtitle: "footprinting:resultsPage.yearOnYearComparison.subtitlePiglet",
        }
      : {
          barChartTooltip: "footprinting:resultsPage.barChart.titleTooltip",
          barChartSubtitle: "footprinting:resultsPage.barChart.subtitle",
          yearOnYearSubtitle: "footprinting:resultsPage.yearOnYearComparison.subtitle",
        };

  return (
    <MainPage
      pageName="Footprinting"
      navigationCrumbs={navigationCrumbs}
      showMobileAlert
      fullWidth
      className="footprint-detailed-breakdown-wrapper margin-bottom-400"
    >
      <Flex vertical gap={16}>
        <Typography variant="h1">{t("footprinting:resultsPage.title")}</Typography>
        {hasResults && (
          <Flex justify="space-between">
            <Flex gap={8}>
              {thisYearCompletedCropProgressQueries.length > 1 ? (
                <CheckboxSegmented
                  key={0}
                  checked={selectedId === null}
                  label={t("footprinting:resultsPage.totalEmission")}
                  onClick={() => setSelectedId(null)}
                />
              ) : null}
              {thisYearCompletedCropProgressQueries.map((cqp) => {
                const cropInfo = cropDetailsQuery.data?.find((cd) => cd.id === cqp.data?.cropId);
                return cropInfo ? (
                  <CheckboxSegmented
                    checked={cropInfo.id === selectedId}
                    key={`${cqp.data?.year}-${cropInfo?.name}`}
                    label={cropInfo?.name}
                    onClick={() => setSelectedId(cropInfo?.id)}
                  />
                ) : null;
              })}
              {beefProgressQueries
                .filter((bpq) => bpq.data?.year === selectedYear && isPercentageComplete(bpq.data.completionPct))
                .map((bpq) => (
                  <CheckboxSegmented
                    checked={bpq.data?.footprint?.id === selectedId}
                    key={`${bpq.data?.year}-beef`}
                    label={t("footprinting:resultsPage.beef")}
                    onClick={() => setSelectedId(bpq.data?.footprint?.id ?? "")}
                  />
                ))}
              {dairyProgressQueries
                .filter((dpq) => dpq.data?.year === selectedYear && isPercentageComplete(dpq.data.completionPct))
                .map((dpq) => (
                  <CheckboxSegmented
                    checked={dpq.data?.footprint?.id === selectedId}
                    key={`${dpq.data?.year}-dairy`}
                    label={t("footprinting:resultsPage.dairy")}
                    onClick={() => setSelectedId(dpq.data?.footprint?.id ?? "")}
                  />
                ))}
              {porkProgressQueries
                .filter((ppq) => ppq.data?.year === selectedYear && isPercentageComplete(ppq.data.completionPct))
                .map((ppq) => (
                  <CheckboxSegmented
                    checked={ppq.data?.footprint?.id === selectedId}
                    key={`${ppq.data?.year}-pork`}
                    label={t("footprinting:resultsPage.pork")}
                    onClick={() => setSelectedId(ppq.data?.footprint?.id ?? "")}
                  />
                ))}
              {pigletProgressQueries
                .filter((ppq) => ppq.data?.year === selectedYear && isPercentageComplete(ppq.data.completionPct))
                .map((ppq) => (
                  <CheckboxSegmented
                    checked={ppq.data?.footprint?.id === selectedId}
                    key={`${ppq.data?.year}-piglet`}
                    label={t("footprinting:resultsPage.piglet")}
                    onClick={() => setSelectedId(ppq.data?.footprint?.id ?? "")}
                  />
                ))}
            </Flex>
            <SelectSegmented
              value={selectedYear}
              onChange={setSelectedYear}
              options={availableYears
                .filter((year) => {
                  if (currentFarm?.carbonProgram?.farmLevelFootprinting?.disabledYears?.includes(year)) {
                    return false;
                  }

                  // Only show year options if that year has a valid footprint to show
                  const farmCompleteForYear = farmProgressQueries.some((fpq) => fpq.data?.year === year && isPercentageComplete(fpq.data?.completionPct ?? 0));
                  const someCropCompleteForYear = cropProgressQueries.some(
                    (cpq) => cpq.data?.year === year && isPercentageComplete(cpq.data?.completionPct ?? 0)
                  );
                  const beefCompleteForYear = beefProgressQueries.some((bpq) => bpq.data?.year === year && isPercentageComplete(bpq.data?.completionPct ?? 0));
                  const dairyCompleteForYear = dairyProgressQueries.some(
                    (dpq) => dpq.data?.year === year && isPercentageComplete(dpq.data?.completionPct ?? 0)
                  );
                  const porkCompleteForYear = porkProgressQueries.some((ppq) => ppq.data?.year === year && isPercentageComplete(ppq.data?.completionPct ?? 0));
                  const pigletCompleteForYear = pigletProgressQueries.some(
                    (ppq) => ppq.data?.year === year && isPercentageComplete(ppq.data?.completionPct ?? 0)
                  );
                  return (
                    farmCompleteForYear &&
                    (beefCompleteForYear || someCropCompleteForYear || dairyCompleteForYear || porkCompleteForYear || pigletCompleteForYear)
                  );
                })
                .map((y) => ({ label: y.toString(), value: y }))}
            />
          </Flex>
        )}

        {showEmptyState ? (
          <EmptyStateCard
            dataTestId="footprinting-results-empty-state"
            iconName="tabler/zoom-cancel"
            title={t("footprinting:resultsPage.emptyState.title")}
            description={t("footprinting:resultsPage.emptyState.description")}
            buttonText={t("footprinting:resultsPage.emptyState.reloadButton")}
            onButtonClick={() => window.location.reload()}
          />
        ) : (
          <>
            <SummaryTiles footprintCalculationSummary={footprintCalculationSummary ?? undefined} hidePerYield={selectedId === null} farmId={currentFarm?.id} />

            <Flex gap={16}>
              <OutlinedCard className="footprint-detailed-breakdown-card" dataTestId="footprint-detailed-breakdown-card">
                <Flex vertical gap={8}>
                  <Typography variant="h5">{t("footprinting:resultsPage.detailedBreakdownTitle")}</Typography>
                  <Typography variant="paragraph-s" color="grey-600">
                    {t("footprinting:resultsPage.detailedBreakdownSubtitle")}
                  </Typography>
                </Flex>

                {/* TODO: Replcae `OutlinedCard` with a flex version so `gap` works correctly */}
                <Flex className="margin-top-100" gap={32} flex={1}>
                  <FootprintingPieChart footprintCalculationSummary={footprintCalculationSummary ?? undefined} />
                  {footprintType === RESULTS_PAGE_TYPES.beef ? (
                    <FootprintingBeefResultTable footprintCalculationSummary={(footprintCalculationSummary as BeefFootprintCalculationSummary) ?? undefined} />
                  ) : footprintType === RESULTS_PAGE_TYPES.dairy ? (
                    <FootprintingDairyResultTable
                      footprintCalculationSummary={(footprintCalculationSummary as DairyFootprintCalculationSummary) ?? undefined}
                    />
                  ) : footprintType === RESULTS_PAGE_TYPES.pork ? (
                    <FootprintingPorkResultTable footprintCalculationSummary={(footprintCalculationSummary as PorkFootprintCalculationSummary) ?? undefined} />
                  ) : footprintType === RESULTS_PAGE_TYPES.piglet ? (
                    <FootprintingPigletResultTable
                      footprintCalculationSummary={(footprintCalculationSummary as PigletFootprintCalculationSummary) ?? undefined}
                    />
                  ) : (
                    <FootprintingCropResultTable
                      hidePerYield={selectedId === null}
                      footprintCalculationSummary={(footprintCalculationSummary as CropFootprintCalculationSummary) ?? undefined}
                    />
                  )}
                </Flex>
              </OutlinedCard>
              <Flex vertical gap={16} className="padding-bottom-350">
                <div className="padding-125 footprinting-results-graph">
                  <BarChart
                    width={320}
                    height={340}
                    title={t("footprinting:resultsPage.barChart.title")}
                    tooltip={t(resultsChartCopyKeys.barChartTooltip)}
                    subtitle={t(resultsChartCopyKeys.barChartSubtitle)}
                    tag={
                      <Flex align="center" gap={4} className="pct-bubble">
                        <Icon
                          iconName={regionalValueComparisonChange! >= 0 ? "tabler/trending-up" : "tabler/trending-down"}
                          color="white"
                          className="icon inline"
                        />
                        {Math.round(Math.abs(regionalValueComparisonChange!))}%
                      </Flex>
                    }
                    bars={[
                      {
                        label: `${t("footprinting:resultsPage.barChart.yourResultLabel")}`,
                        value: footprintCalculationSummary?.overall.perKgYieldCO2e || 0,
                        color: "#065C45", // green
                      },
                      {
                        label: `${t("footprinting:resultsPage.barChart.regionalLabel")}`,
                        value: regionalValue || 0,
                        color: "#B4A8C8", // purple
                      },
                    ]}
                    formatFunction={(value: number) => `${value.toFixed(2)}`}
                  />
                </div>
                <div className="padding-125 footprinting-results-graph">
                  {footprintType === RESULTS_PAGE_TYPES.beef ||
                  footprintType === RESULTS_PAGE_TYPES.crop ||
                  footprintType === RESULTS_PAGE_TYPES.dairy ||
                  footprintType === RESULTS_PAGE_TYPES.pork ||
                  footprintType === RESULTS_PAGE_TYPES.piglet ? (
                    <LineChart
                      labels={yoyChartDataset.map((summary) => summary.year.toString())}
                      width={320}
                      height={340}
                      title={t("footprinting:resultsPage.yearOnYearComparison.title")}
                      subtitle={t(resultsChartCopyKeys.yearOnYearSubtitle)}
                      lines={
                        yoyChartDataset.length > 0
                          ? [
                              {
                                label:
                                  footprintType === RESULTS_PAGE_TYPES.beef
                                    ? t("footprinting:resultsPage.beef")
                                    : footprintType === RESULTS_PAGE_TYPES.dairy
                                    ? t("footprinting:resultsPage.dairy")
                                    : footprintType === RESULTS_PAGE_TYPES.pork
                                    ? t("footprinting:resultsPage.pork")
                                    : footprintType === RESULTS_PAGE_TYPES.piglet
                                    ? t("footprinting:resultsPage.piglet")
                                    : selectedCropCalculationSummariesForAllYears[0].cropName,
                                points: yoyChartDataset.map((summary) => ({
                                  value: summary.footprint?.overall.perKgYieldCO2e ?? 0,
                                })),
                              },
                            ]
                          : []
                      }
                    />
                  ) : null}
                </div>
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </MainPage>
  );
};

export default FootprintingResultsPage;
